import { memo, useCallback, useMemo } from 'react';

import Button from 'components/Button/Button';

import {
  IOffersFilterBarConfig,
  OffersFilterId
} from '../OfferFilters.types';
import { useOffersFilters } from '../hooks/useOffersFilters';

import { CreatedAtFilter } from './Filters/CreatedAtFilter';
import { OfferDesignFilter } from './Filters/OfferDesignFilter';
import { PriceFilter } from './Filters/PriceFilter';
import { ScheduleFilter } from './Filters/ScheduleFilter';
import { SearchFilter } from './Filters/SearchFilter';
import { StatusFilter } from './Filters/StatusFilter';

type IOffersFilterBarProps = IOffersFilterBarConfig;

export const OffersFilterBar = memo(
  ({
    offerDesignType,
    hiddenFilters = []
  }: IOffersFilterBarProps) => {
    const {
      filters,
      rawOfferDesignIds,
      hasActiveFilters,
      setSearch,
      setStatuses,
      setSchedules,
      setOfferDesignIds,
      setCreatedAtRange,
      setPriceBuckets,
      clearAllFilters
    } = useOffersFilters();

    const hiddenSet = useMemo(
      (): Set<OffersFilterId> => new Set(hiddenFilters),
      [hiddenFilters]
    );
    const isVisible = useCallback(
      (id: OffersFilterId): boolean => !hiddenSet.has(id),
      [hiddenSet]
    );

    return (
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="offers-filter-bar"
      >
        {isVisible(OffersFilterId.SEARCH) && (
          <SearchFilter value={filters.search} onChange={setSearch} />
        )}

        {isVisible(OffersFilterId.STATUS) && (
          <StatusFilter selected={filters.statuses} onSelectionChange={setStatuses} />
        )}

        {isVisible(OffersFilterId.SCHEDULE) && (
          <ScheduleFilter
            selected={filters.schedules}
            onSelectionChange={setSchedules}
          />
        )}

        {isVisible(OffersFilterId.OFFER_DESIGN) && (
          <OfferDesignFilter
            offerDesignType={offerDesignType}
            selected={rawOfferDesignIds}
            onSelectionChange={setOfferDesignIds}
          />
        )}

        {isVisible(OffersFilterId.CREATED_AT) && (
          <CreatedAtFilter
            from={filters.createdAtFrom}
            until={filters.createdAtUntil}
            onChange={setCreatedAtRange}
          />
        )}

        {isVisible(OffersFilterId.PRICE) && (
          <PriceFilter
            selected={filters.priceBuckets}
            onSelectionChange={setPriceBuckets}
          />
        )}

        {hasActiveFilters && (
          <Button
            variant="text"
            onClick={clearAllFilters}
            dataTestId="offers-filter-bar-clear"
            className="text-base-primary border-none hover:bg-none text-indigo-600 text-xs"
          >
            Clear filters
          </Button>
        )}
      </div>
    );
  }
);

OffersFilterBar.displayName = 'OffersFilterBar';
